import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TroubleshootOutlinedIcon from "@mui/icons-material/TroubleshootOutlined";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import Popover from "@mui/material/Popover";
import QRCode from "react-qr-code";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import * as global from "../data/global";
import WindowChrome, { WindowChromeStatusBanner } from "./windowChrome/WindowChrome.js";
import { getWindowChromeThemeColor } from "./windowChrome/windowChromeTheme.js";
import { comparisonFieldSx, outlinedActionButtonSx, containedActionButtonSx } from "./windowChrome/actionButtons.js";
import { SITE_WINDOW_IDS, CCR_DEFAULT_ASSET_TAG } from "./windowChrome/siteWindowNav.js";
import CcrPartSelect, { partDetail } from "./CcrPartSelect.js";

const CcrSectionContext = createContext({
    isOpen: () => false,
    toggle: () => {},
});

function ccrButtonSx(extra) {
    return { ...containedActionButtonSx(), ...extra };
}

function addFieldSx() {
    const base = comparisonFieldSx();
    return {
        ...base,
        width: { xs: "100%", sm: 180 },
        flex: { xs: "1 1 140px", sm: "0 0 180px" },
        "& .MuiOutlinedInput-root": {
            ...base["& .MuiOutlinedInput-root"],
            height: 40,
            alignItems: "center",
        },
        "& .MuiInputBase-input": {
            ...base["& .MuiInputBase-input"],
            paddingTop: 0,
            paddingBottom: 0,
            height: 24,
            boxSizing: "border-box",
        },
    };
}

const TONE = {
    operational: { mark: "🟢", color: "#2e7d32" },
    attention: { mark: "🟡", color: "#f9a825" },
    action: { mark: "🔴", color: "#c62828" },
    unknown: { mark: "⚪", color: "#9e9e9e" },
};

const CADENCE_LABEL = {
    Daily: "Daily / Shift",
    Weekly: "Weekly / Bi-weekly",
    Monthly: "Monthly",
};

const CADENCE_OPTIONS = [
    ["Daily", "Daily / Shift"],
    ["Weekly", "Weekly / Bi-weekly"],
    ["Monthly", "Monthly"],
];

const RESULT_KIND_OPTIONS = [
    ["", "Does not update a date"],
    ["Inspect", "Inspection"],
    ["Clean", "Cleaning"],
    ["Adjust", "Adjustment"],
];

const CONDITION_OPTIONS = [
    ["NotInspected", "Not Inspected"],
    ["Ok", "OK"],
    ["Attention", "Attention"],
    ["Action", "Action Required"],
    ["Unknown", "Unknown"],
];

const STATUS_OPTIONS = [
    ["Unknown", "Unknown"],
    ["Operational", "Operational"],
    ["Attention", "Attention"],
    ["ActionRequired", "Action Required"],
];

const EVENT_TYPES = ["Inspection", "PM", "Calibration", "Repair", "Photo", "Note"];

const CCR_LABEL_BASE_URL = "https://grainwave.org";

const FIELD_HELP = {
    areaName: "Plant area this instrument belongs to.",
    assetType: "What kind of instrument this is.",
    status: "Operational, needs attention, or needs action.",
    physicalLocation: "Where this unit is installed.",
    processFunction: "What this instrument does in the process.",
    manufacturer: "Who made this unit.",
    model: "Manufacturer model.",
    partNumber: "Catalog number. Other units can share it. Each unit keeps its own CCR.",
    serialNumber: "Serial number on this physical unit.",
    measurementRange: "Range this instrument is set up to measure.",
    outputProtocol: "How the reading leaves the instrument, such as 4-20mA HART.",
    plcDcsDestination: "Where the signal lands in the PLC or DCS.",
    drawingPid: "Drawing or P&ID that shows this instrument.",
    hazardousAreaClass: "Hazardous-area rating from the nameplate.",
    oemManualStatus: "Whether the OEM manual is on file.",
    criticality: "How important this instrument is to the process.",
};

const RELIEF_HELP = {
    spareAvailable: "Is a spare on hand if this instrument fails?",
    spareLocation: "Where that spare is kept.",
    spareVerifiedGood: "Has someone confirmed the spare works?",
    exactReplacementKnown: "Do we know the exact replacement part?",
    approvedSubstitute: "An approved stand-in if the exact part is unavailable.",
    vendorSource: "Who we buy the replacement from.",
    leadTime: "How long a replacement takes to arrive.",
    requiresShutdown: "Whether swapping it requires a shutdown.",
    replacementProcedure: "How the failed instrument is replaced.",
    configurationRequired: "Settings that have to be loaded after replacement.",
    repairRebuildPossible: "Whether this unit can be repaired instead of replaced.",
};

function captureName(file, fallbackExt) {
    const name = String(file?.name || "");
    if (/\.(mp4|mov|m4v|webm|3gp|3gpp|qt|jpg|jpeg|png|gif|webp|heic|bmp)$/i.test(name)) return name;
    const type = String(file?.type || "").toLowerCase();
    if (type.includes("mp4") || type.includes("3gpp") || type.startsWith("video/")) return `capture-${Date.now()}.mp4`;
    if (type.startsWith("image/")) return `capture-${Date.now()}.jpg`;
    return `capture-${Date.now()}.${fallbackExt}`;
}

function uploadFailureMessage(response, text, payload) {
    if (payload?.error) return String(payload.error);
    const body = String(text || "");
    if (response.status === 413 || /maxAllowedContentLength|request length|404\.13|entity too large/i.test(body)) {
        return "That video is too large to save. The limit is 2 GB.";
    }
    const trimmed = body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (trimmed && trimmed.length < 180) return trimmed;
    return `Could not save the file (${response.status}).`;
}

function ccrLabelUrl(siteGuid, assetTag) {
    if (!siteGuid || !assetTag) return "";
    return `${CCR_LABEL_BASE_URL}/ccr/${siteGuid}/${encodeURIComponent(assetTag)}`;
}

function Tip({ title, children, fill = false }) {
    if (!title) return children;
    return (
        <MuiMaterials.Tooltip title={title} arrow>
            <MuiMaterials.Box
                component="span"
                sx={fill ? {
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                    "& .MuiFormControl-root": { width: "100%" },
                } : {
                    display: "inline-flex",
                    maxWidth: "100%",
                }}
            >
                {children}
            </MuiMaterials.Box>
        </MuiMaterials.Tooltip>
    );
}

function CcrLabelContent({ tag, url, lines }) {
    if (!tag) return null;
    return (
        <div
            style={{
                color: "#111",
                background: "#fff",
                fontFamily: "Arial, Helvetica, sans-serif",
                WebkitTextFillColor: "#111",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
            }}
        >
            <div
                className="ccr-label-cutout"
                style={{
                    border: "2px dashed #333",
                    padding: "10px 8px",
                    width: "2.75in",
                    boxSizing: "border-box",
                    marginBottom: "12px",
                    textAlign: "center",
                    background: "#fff",
                }}
            >
                <div style={{ fontWeight: 700, fontSize: "15px", lineHeight: 1.25, marginBottom: "10px", wordBreak: "break-word" }}>
                    {tag}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <QRCode value={url || tag} size={120} level="M" bgColor="#ffffff" fgColor="#000000" />
                </div>
            </div>
            <div className="ccr-label-reference" style={{ fontSize: "11px", maxWidth: "3.5in", lineHeight: 1.35 }}>
                {(lines || []).filter(Boolean).map((line, index) => (
                    <div key={`${index}-${line}`}>{line}</div>
                ))}
            </div>
        </div>
    );
}

const HIDDEN_SCROLL_SX = {
    flex: 1,
    minHeight: 0,
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 },
    // Column flex children shrink to the viewport and clip. Keep each block at its content height so this box scrolls.
    "& > *": { flexShrink: 0 },
};

const IDENTITY_FIELDS = [
    ["physicalLocation", "Physical Location"],
    ["processFunction", "Process Function"],
    ["manufacturer", "Manufacturer"],
    ["model", "Model"],
    ["partNumber", "Part Number"],
    ["serialNumber", "Serial Number"],
    ["measurementRange", "Measurement Range"],
    ["outputProtocol", "Output / Protocol"],
    ["plcDcsDestination", "PLC/DCS Destination"],
    ["drawingPid", "Drawing / P&ID"],
    ["hazardousAreaClass", "Hazardous Area Classification"],
    ["oemManualStatus", "OEM Manual"],
    ["criticality", "Criticality"],
];

const RELIEF_FIELDS = [
    ["spareAvailable", "Spare Available?"],
    ["spareLocation", "Spare Location"],
    ["spareVerifiedGood", "Spare Verified Good"],
    ["exactReplacementKnown", "Exact Replacement Known"],
    ["approvedSubstitute", "Approved Substitute"],
    ["vendorSource", "Vendor / Source"],
    ["leadTime", "Current Lead Time"],
    ["requiresShutdown", "Replacement Requires Shutdown"],
    ["replacementProcedure", "Replacement Procedure"],
    ["configurationRequired", "Configuration Required"],
    ["repairRebuildPossible", "Repair / Rebuild Possible"],
];

function toneOf(score) {
    return TONE[score?.tone] || TONE.unknown;
}

function emptyIdentity(asset) {
    const next = {
        areaName: asset?.areaName || "",
        assetType: asset?.assetType || "",
        status: asset?.status || "Unknown",
    };
    IDENTITY_FIELDS.forEach(([key]) => {
        next[key] = asset?.[key] || "";
    });
    return next;
}

function emptyRelief(relief) {
    const next = {};
    RELIEF_FIELDS.forEach(([key]) => {
        next[key] = relief?.[key] || "";
    });
    return next;
}

export default function CcrInstrumentWindow() {
    const { siteGuid, assetTag: routeAssetTag } = useParams();
    const navigate = useNavigate();
    const decodedTag = routeAssetTag || "";
    const [themeVersion, setThemeVersion] = useState(0);
    const [record, setRecord] = useState(null);
    const [assetTag, setAssetTag] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [identity, setIdentity] = useState(null);
    const [relief, setRelief] = useState(null);
    const [pmOpen, setPmOpen] = useState(false);
    const [pmNotes, setPmNotes] = useState("");
    const [pmItems, setPmItems] = useState({});
    const [pmConditions, setPmConditions] = useState({});
    const [signalId, setSignalId] = useState(null);
    const [signalDetail, setSignalDetail] = useState("");
    const [eventType, setEventType] = useState("Inspection");
    const [eventFinding, setEventFinding] = useState("");
    const [eventAction, setEventAction] = useState("");
    const [uploadNote, setUploadNote] = useState("");
    const [addAnchor, setAddAnchor] = useState(null);
    const [whatAnchor, setWhatAnchor] = useState(null);
    const [whatType, setWhatType] = useState("");
    const [whatPart, setWhatPart] = useState("");
    const [whatSerial, setWhatSerial] = useState("");
    const [whatLocation, setWhatLocation] = useState("");
    const [newTag, setNewTag] = useState("");
    const [newArea, setNewArea] = useState("");
    const [newType, setNewType] = useState("");
    const [newPart, setNewPart] = useState("");
    const [newSerial, setNewSerial] = useState("");
    const [openSections, setOpenSections] = useState({});
    const [labelOpen, setLabelOpen] = useState(false);
    const scrollerRef = useRef(null);
    const pendingScroll = useRef("");
    const fileRef = useRef(null);
    const docFileRef = useRef(null);
    const docTargetRef = useRef(null);
    const cameraRef = useRef(null);
    const videoRef = useRef(null);
    const cameraTargetRef = useRef(null);

    const revealSection = (id) => {
        pendingScroll.current = id;
        setOpenSections((prev) => ({ ...prev, [id]: true }));
    };

    useLayoutEffect(() => {
        const id = pendingScroll.current;
        if (!id) return;
        const root = scrollerRef.current;
        const el = document.getElementById(id);
        if (!root || !el) return;
        pendingScroll.current = "";
        const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 8;
        root.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, [openSections]);

    const sectionApi = useMemo(() => ({
        isOpen: (id, defaultOpen) => (
            Object.prototype.hasOwnProperty.call(openSections, id) ? openSections[id] : !!defaultOpen
        ),
        toggle: (id, defaultOpen) => setOpenSections((prev) => {
            const current = Object.prototype.hasOwnProperty.call(prev, id) ? prev[id] : !!defaultOpen;
            return { ...prev, [id]: !current };
        }),
        setOpen: (id, value) => setOpenSections((prev) => ({ ...prev, [id]: value })),
    }), [openSections]);

    useEffect(() => {
        const onTheme = () => setThemeVersion((value) => value + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    const text = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const muted = getWindowChromeThemeColor("#666666", "#c4a574");
    const paper = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const border = getWindowChromeThemeColor("#d0d0d0", "#f4cc8e55");
    void themeVersion;

    const applyRecord = useCallback((next, keepTag) => {
        setRecord(next);
        const tag = keepTag || next?.asset?.assetTag || "";
        setAssetTag(tag);
        setIdentity(next?.asset ? emptyIdentity(next.asset) : null);
        setRelief(next?.relief ? emptyRelief(next.relief) : null);
        if (next?.openSession) {
            setPmOpen(true);
        }
    }, []);

    const load = useCallback(async (tag) => {
        if (!siteGuid) return;
        setLoading(true);
        setError("");
        const next = await createApiEndpoint().fetchCcrInstrument("direct", siteGuid, tag || "");
        if (next?.error) {
            setError(next.error);
            setRecord(null);
        } else {
            applyRecord(next, tag);
        }
        setLoading(false);
    }, [applyRecord, siteGuid]);

    useEffect(() => {
        if (!siteGuid) return;
        if (!decodedTag) {
            navigate(`/ccr/${siteGuid}/${encodeURIComponent(CCR_DEFAULT_ASSET_TAG)}`, { replace: true });
            return;
        }
        load(decodedTag);
    }, [load, decodedTag, siteGuid, navigate]);

    const pmTagRef = useRef("");
    useEffect(() => {
        if (!record?.openSession) return;
        const tag = record.asset?.assetTag || "";
        const tagChanged = pmTagRef.current !== tag;
        if (tagChanged) pmTagRef.current = tag;
        setPmItems((prev) => {
            if (!tagChanged && Object.keys(prev).length > 0) return prev;
            const itemState = {};
            (record.pmItems || []).forEach((item) => {
                itemState[item.id] = { checked: false, condition: "NotInspected", finding: "" };
            });
            return itemState;
        });
        setPmConditions((prev) => {
            if (!tagChanged && Object.keys(prev).length > 0) return prev;
            const conditionState = {};
            (record.conditions || []).forEach((row) => {
                conditionState[row.key] = { status: row.status || "NotInspected", finding: row.finding || "" };
            });
            return conditionState;
        });
    }, [record]);

    const save = useCallback(async (body) => {
        setBusy(true);
        setError("");
        const next = await createApiEndpoint().saveCcrInstrument("direct", siteGuid, {
            assetTag: body.assetTag || assetTag,
            ...body,
        });
        setBusy(false);
        if (next?.error) {
            setError(next.error);
            return null;
        }
        applyRecord(next, next?.asset?.assetTag || body.assetTag);
        return next;
    }, [applyRecord, assetTag, siteGuid]);

    const asset = record?.asset;
    const scores = record?.scores;
    const selectedPart = (record?.assets || []).find((part) => String(part?.assetTag || "") === String(asset?.assetTag || "")) || asset;
    const identityLine = partDetail(selectedPart);
    const selectedPartNumber = String(selectedPart?.partNumber || "").trim();
    const selectedSerial = String(selectedPart?.serialNumber || "").trim();
    const samePartNumber = selectedPartNumber
        ? (record?.assets || []).filter((part) => String(part?.partNumber || "").trim().toLowerCase() === selectedPartNumber.toLowerCase())
        : [];
    const labelUrl = ccrLabelUrl(siteGuid, asset?.assetTag);
    const labelLines = [
        identityLine,
        selectedSerial ? `Serial ${selectedSerial}` : "",
        [selectedPart?.manufacturer, selectedPart?.model].filter(Boolean).join(" "),
        record?.facility || "",
    ];

    const checklist = useMemo(() => {
        const groups = [];
        (record?.pmItems || []).forEach((item) => {
            let group = groups.find((g) => g.cadence === item.cadence);
            if (!group) {
                group = { cadence: item.cadence, items: [] };
                groups.push(group);
            }
            group.items.push(item);
        });
        return groups;
    }, [record]);

    const beginPm = async () => {
        const next = await save({ section: "startPm" });
        if (!next?.openSession) return;
        const itemState = {};
        (next.pmItems || []).forEach((item) => {
            itemState[item.id] = { checked: false, condition: "NotInspected", finding: "" };
        });
        const conditionState = {};
        (next.conditions || []).forEach((row) => {
            conditionState[row.key] = { status: row.status || "NotInspected", finding: row.finding || "" };
        });
        setPmItems(itemState);
        setPmConditions(conditionState);
        setPmNotes("");
        setPmOpen(true);
        revealSection("ccr-pm");
    };

    const completePm = async () => {
        if (!record?.openSession) return;
        const items = (record.pmItems || []).map((item) => ({
            pmItemId: item.id,
            checked: !!pmItems[item.id]?.checked,
            condition: pmItems[item.id]?.condition || "NotInspected",
            finding: pmItems[item.id]?.finding || "",
        }));
        const conditions = (record.conditions || []).map((row) => ({
            key: row.key,
            status: pmConditions[row.key]?.status || row.status,
            finding: pmConditions[row.key]?.finding || "",
        }));
        const next = await save({
            section: "completePm",
            sessionId: record.openSession.id,
            notes: pmNotes,
            items,
            conditions,
        });
        if (next && !next.error) setPmOpen(false);
    };

    const uploadMedia = async (file, fileName) => {
        if (!file || !siteGuid || !asset?.assetTag) return;
        if (!file.size) {
            setError("The phone did not provide the file. Take it again.");
            setUploadNote("");
            return;
        }
        setBusy(true);
        setError("");
        const mb = Math.max(1, Math.round(file.size / (1024 * 1024)));
        setUploadNote(`Sending ${mb} MB…`);
        try {
            const body = new FormData();
            body.append("siteId", siteGuid);
            body.append("assetTag", asset.assetTag);
            body.append("file", file, fileName || file.name || "capture.jpg");
            const response = await fetch(`${global.ApiUrl}/api/ccr-media`, {
                method: "POST",
                headers: { Authorization: `Bearer ${global.gw.token}` },
                body,
            });
            const text = await response.text();
            let payload = null;
            try {
                payload = text ? JSON.parse(text) : null;
            } catch {
                payload = null;
            }
            if (!response.ok || payload?.error) {
                const message = uploadFailureMessage(response, text, payload);
                setError(message);
                setUploadNote(message);
            } else {
                setUploadNote("");
                await load(asset.assetTag);
            }
        } catch (err) {
            const message = err?.message || "Could not save the file.";
            setError(message);
            setUploadNote(message);
        }
        setBusy(false);
    };

    const uploadDocument = async (file, documentId) => {
        if (!file || !siteGuid || !asset?.assetTag || !documentId) return;
        setBusy(true);
        setError("");
        try {
            const body = new FormData();
            body.append("siteId", siteGuid);
            body.append("assetTag", asset.assetTag);
            body.append("documentId", String(documentId));
            body.append("file", file);
            const response = await fetch(`${global.ApiUrl}/api/ccr-document`, {
                method: "POST",
                headers: { Authorization: `Bearer ${global.gw.token}` },
                body,
            });
            const payload = await response.json().catch(() => null);
            if (!response.ok || payload?.error) {
                setError(payload?.error || payload || "Could not attach the file.");
            } else {
                await load(asset.assetTag);
            }
        } catch (err) {
            setError(err?.message || "Could not attach the file.");
        }
        setBusy(false);
    };

    const openDocument = async (doc) => {
        if (doc?.url) {
            window.open(doc.url, "_blank", "noopener");
            return;
        }
        if (!siteGuid || !doc?.id) return;
        setError("");
        try {
            const response = await fetch(`${global.ApiUrl}/api/ccr-document/${siteGuid}/${doc.id}`, {
                headers: { Authorization: `Bearer ${global.gw.token}` },
            });
            if (!response.ok) {
                setError("Could not open the file.");
                return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const opened = window.open(url, "_blank", "noopener");
            if (!opened) {
                const link = document.createElement("a");
                link.href = url;
                link.download = doc.fileName || doc.category || "document";
                link.click();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            setError(err?.message || "Could not open the file.");
        }
    };

    if (!siteGuid) {
        return (
            <WindowChrome title="CCR" toolTitle="CCR" subtitle="Instrument record">
                <MuiMaterials.Box sx={{ p: 3, color: text }}>Open a CCR site from the site list.</MuiMaterials.Box>
            </WindowChrome>
        );
    }

    const printLabel = () => {
        requestAnimationFrame(() => {
            window.print();
        });
    };

    return (
        <>
        <style>
            {`
            #ccr-label-print-portal {
                position: fixed;
                left: -10000px;
                top: 0;
                width: 3.5in;
                padding: 0.25in;
                background: #fff;
                z-index: -1;
            }
            .ccr-label-cutout {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            @media print {
                @page { margin: 0.25in; }
                body * { visibility: hidden !important; }
                #ccr-label-print-portal,
                #ccr-label-print-portal * {
                    visibility: visible !important;
                }
                #ccr-label-print-portal {
                    position: fixed !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 3.5in !important;
                    padding: 0.25in !important;
                    background: #fff !important;
                    z-index: 99999 !important;
                }
                .ccr-label-cutout {
                    border: 2px dashed #000 !important;
                    background: #fff !important;
                }
                .ccr-screen-only { display: none !important; }
            }
            `}
        </style>
        {labelOpen && asset?.assetTag ? (
            <div id="ccr-label-print-portal" aria-hidden="true">
                <CcrLabelContent tag={asset.assetTag} url={labelUrl} lines={labelLines} />
            </div>
        ) : null}
        <WindowChrome
            className="ccr-screen-only"
            title="CCR"
            toolTitle="CCR"
            siteWindowId={SITE_WINDOW_IDS.ccr}
            siteGuid={siteGuid}
            showHeaderSiteSelect
            headerFilterSite={(site) => site?.isCcr === true}
            onHeaderSiteChange={(guid) => {
                if (!guid || guid === siteGuid) return;
                navigate(`/ccr/${guid}`);
            }}
            actions={(
                <>
                <Tip title="Reload this instrument.">
                    <MuiMaterials.Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        disabled={loading || !decodedTag}
                        onClick={() => load(decodedTag)}
                        sx={outlinedActionButtonSx()}
                    >
                        Refresh
                    </MuiMaterials.Button>
                </Tip>
                <Tip title="Creates another instrument on this site, with its own CCR. A part number can match one already here. The tag has to be new.">
                    <MuiMaterials.Button
                        variant="contained"
                        size="small"
                        onClick={(e) => setAddAnchor(e.currentTarget)}
                        sx={containedActionButtonSx()}
                    >
                        Add
                    </MuiMaterials.Button>
                </Tip>
                {asset?.assetTag ? (
                    <Tip title="Print a cut-out label for this instrument. The dashed box is the sticker. The code opens this CCR record.">
                        <MuiMaterials.Button
                            variant="outlined"
                            size="small"
                            startIcon={<PrintIcon />}
                            onClick={() => setLabelOpen(true)}
                            sx={outlinedActionButtonSx()}
                        >
                            Print label
                        </MuiMaterials.Button>
                    </Tip>
                ) : null}
                </>
            )}
            statusBanner={error ? <WindowChromeStatusBanner status={error} onDismiss={() => setError("")} /> : null}
        >
            <CcrSectionContext.Provider value={sectionApi}>
            <MuiMaterials.Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: getWindowChromeThemeColor("#e8e8e8", "#1a1a1a") }}>
            {asset ? (
                <MuiMaterials.Box
                    sx={{
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 0.25,
                        mx: 2,
                        mt: 2,
                        mb: 0,
                        px: { xs: 1.5, sm: 2 },
                        py: 1.25,
                        borderRadius: "12px",
                        border: `1px solid ${getWindowChromeThemeColor("rgba(0, 0, 0, 0.12)", "rgba(244, 204, 142, 0.28)")}`,
                        background: getWindowChromeThemeColor("#f8f8f8", "#313131"),
                        boxShadow: getWindowChromeThemeColor("0 2px 8px rgba(0, 0, 0, 0.04)", "0 4px 16px rgba(0, 0, 0, 0.2)"),
                    }}
                >
                    <CcrPartSelect
                        parts={record?.assets || []}
                        value={asset.assetTag || decodedTag}
                        onChange={(tag) => navigate(`/ccr/${siteGuid}/${encodeURIComponent(tag)}`)}
                    />
                    <MuiMaterials.Tooltip title="Edit the type, part number, and location. Other instruments can use this same part number. Each one keeps its own CCR." arrow>
                        <MuiMaterials.Box
                            component="button"
                            type="button"
                            onClick={(e) => {
                                setWhatType(identity?.assetType || asset.assetType || "");
                                setWhatPart(identity?.partNumber || asset.partNumber || "");
                                setWhatSerial(identity?.serialNumber || asset.serialNumber || "");
                                setWhatLocation(identity?.physicalLocation || asset.physicalLocation || "");
                                setWhatAnchor(e.currentTarget);
                            }}
                            sx={{
                                m: 0,
                                px: 0.5,
                                py: 0,
                                border: 0,
                                background: "transparent",
                                cursor: "pointer",
                                textAlign: "left",
                                fontFamily: "inherit",
                                fontSize: 13,
                                fontWeight: 500,
                                lineHeight: 1.35,
                                color: identityLine ? text : muted,
                                WebkitTextFillColor: identityLine ? text : muted,
                            }}
                        >
                            {identityLine || "Type · Part · Location"}
                        </MuiMaterials.Box>
                    </MuiMaterials.Tooltip>
                    {samePartNumber.length > 1 ? (
                        <Tip title="Several physical units share this catalog number. The serial tells them apart. Each one has its own CCR." fill>
                        <MuiMaterials.Typography sx={{ px: 0.5, fontSize: 12, color: muted, lineHeight: 1.35 }}>
                            {selectedSerial ? `Serial ${selectedSerial}` : "No serial yet"}
                            {` · ${samePartNumber.length} instruments share part ${selectedPartNumber}, each with its own CCR`}
                        </MuiMaterials.Typography>
                        </Tip>
                    ) : null}
                    <Popover
                        open={Boolean(whatAnchor)}
                        anchorEl={whatAnchor}
                        onClose={() => setWhatAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{
                            sx: {
                                p: 1.5,
                                mt: 0.5,
                                background: paper,
                                color: text,
                                border: `1px solid ${border}`,
                                maxWidth: "min(96vw, 720px)",
                            },
                        }}
                    >
                        <MuiMaterials.Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                            <Tip title={FIELD_HELP.assetType}><MuiMaterials.TextField size="small" label="Type" value={whatType} onChange={(e) => setWhatType(e.target.value)} sx={addFieldSx()} /></Tip>
                            <Tip title={FIELD_HELP.partNumber}><MuiMaterials.TextField size="small" label="Part number" value={whatPart} onChange={(e) => setWhatPart(e.target.value)} sx={addFieldSx()} /></Tip>
                            <Tip title={FIELD_HELP.physicalLocation}><MuiMaterials.TextField size="small" label="Location" value={whatLocation} onChange={(e) => setWhatLocation(e.target.value)} sx={addFieldSx()} /></Tip>
                            <Tip title={FIELD_HELP.serialNumber}><MuiMaterials.TextField size="small" label="Serial number" value={whatSerial} onChange={(e) => setWhatSerial(e.target.value)} sx={addFieldSx()} /></Tip>
                            <Tip title="Save type, part number, location, and serial on this instrument.">
                            <MuiMaterials.Button
                                variant="contained"
                                size="small"
                                disabled={busy || !identity}
                                sx={ccrButtonSx({ flexShrink: 0 })}
                                onClick={async () => {
                                    const next = { ...identity, assetType: whatType, partNumber: whatPart, serialNumber: whatSerial, physicalLocation: whatLocation };
                                    setIdentity(next);
                                    const saved = await save({ section: "asset", ...next });
                                    if (saved && !saved.error) setWhatAnchor(null);
                                }}
                            >
                                Save
                            </MuiMaterials.Button>
                            </Tip>
                        </MuiMaterials.Box>
                    </Popover>
                </MuiMaterials.Box>
            ) : null}
            <MuiMaterials.Box
                ref={scrollerRef}
                className="hide-scrollbar"
                sx={{
                    ...HIDDEN_SCROLL_SX,
                    height: "auto",
                    px: 2,
                    pt: 2,
                    pb: { xs: 4, md: 5 },
                    color: text,
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 2, sm: 2.5 },
                }}
            >
                {loading && <MuiMaterials.Typography>Loading instrument record…</MuiMaterials.Typography>}
                {!loading && record && !asset && (
                    <Section id="ccr-empty" title="No instruments yet" help="This site has no instruments yet. Add creates the first record, with its own CCR." paper={paper} border={border} defaultOpen>
                        <MuiMaterials.Typography sx={{ color: muted, mb: 1 }}>
                            Add the first instrument for this facility. Each later asset is another record in this same database.
                        </MuiMaterials.Typography>
                        <AddInstrument
                            newTag={newTag}
                            newArea={newArea}
                            newType={newType}
                            newPart={newPart}
                            newSerial={newSerial}
                            setNewTag={setNewTag}
                            setNewArea={setNewArea}
                            setNewType={setNewType}
                            setNewPart={setNewPart}
                            setNewSerial={setNewSerial}
                            busy={busy}
                            onAdd={() => save({ section: "addAsset", assetTag: newTag, areaName: newArea, assetType: newType, partNumber: newPart, serialNumber: newSerial })}
                        />
                    </Section>
                )}
                {!loading && asset && (
                    <>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*,video/*"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                const send = async () => {
                                    if (file) await uploadMedia(file, captureName(file, file.type?.startsWith("video/") ? "mp4" : "jpg"));
                                };
                                send().finally(() => {
                                    e.target.value = "";
                                });
                            }}
                        />
                        <input
                            ref={docFileRef}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.txt,.csv,.doc,.docx,.xls,.xlsx,.zip,application/pdf,image/*,text/*"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                const documentId = docTargetRef.current;
                                docTargetRef.current = null;
                                e.target.value = "";
                                if (file && documentId) uploadDocument(file, documentId);
                            }}
                        />
                        <input
                            ref={cameraRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                const target = cameraTargetRef.current;
                                cameraTargetRef.current = null;
                                const send = async () => {
                                    if (!file) return;
                                    if (target === "media") await uploadMedia(file, captureName(file, "jpg"));
                                    else if (target) await uploadDocument(file, target);
                                };
                                send().finally(() => {
                                    e.target.value = "";
                                });
                            }}
                        />
                        <input
                            ref={videoRef}
                            type="file"
                            accept="video/*"
                            capture="environment"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                const send = async () => {
                                    if (!file) {
                                        setError("The phone did not return a video.");
                                        setUploadNote("The phone did not return a video.");
                                        return;
                                    }
                                    await uploadMedia(file, captureName(file, "mp4"));
                                };
                                send().finally(() => {
                                    e.target.value = "";
                                });
                            }}
                        />
                        <Popover
                            open={Boolean(addAnchor)}
                            anchorEl={addAnchor}
                            onClose={() => setAddAnchor(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                            PaperProps={{
                                sx: {
                                    p: 1.5,
                                    mt: 0.5,
                                    background: paper,
                                    color: text,
                                    border: `1px solid ${border}`,
                                    maxWidth: "min(96vw, 720px)",
                                },
                            }}
                        >
                            <AddInstrument
                                newTag={newTag}
                                newArea={newArea}
                                newType={newType}
                                newPart={newPart}
                                newSerial={newSerial}
                                setNewTag={setNewTag}
                                setNewArea={setNewArea}
                                setNewType={setNewType}
                                setNewPart={setNewPart}
                                setNewSerial={setNewSerial}
                                busy={busy}
                                onAdd={async () => {
                                    const next = await save({ section: "addAsset", assetTag: newTag, areaName: newArea, assetType: newType, partNumber: newPart, serialNumber: newSerial });
                                    if (next && !next.error) {
                                        setAddAnchor(null);
                                        setNewTag("");
                                        setNewArea("");
                                        setNewType("");
                                        setNewPart("");
                                        setNewSerial("");
                                        const tag = next?.asset?.assetTag || newTag;
                                        if (tag) navigate(`/ccr/${siteGuid}/${encodeURIComponent(tag)}`);
                                    }
                                }}
                            />
                        </Popover>

                        <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: { xs: 1.25, sm: 1.5 } }}>
                            <ScoreCard name="Certainty" score={scores?.certainty} detail={scores?.certainty ? `${scores.certainty.percent}% — ${scores.certainty.label}` : ""} />
                            <ScoreCard name="Control" score={scores?.control} detail={scores?.control?.label} />
                            <ScoreCard name="Relief" score={scores?.relief} detail={scores?.relief?.label} />
                            <ScoreCard name="Condition" score={scores?.condition} detail={scores?.condition?.label} />
                        </MuiMaterials.Box>

                        <Tip title="The next useful thing to do on this instrument, based on what is still unknown." fill>
                        <MuiMaterials.Box
                            sx={{
                                p: { xs: "14px 16px", sm: "18px 22px" },
                                borderRadius: "12px",
                                border: `1px solid ${getWindowChromeThemeColor("rgba(0, 0, 0, 0.12)", "rgba(244, 204, 142, 0.28)")}`,
                                background: getWindowChromeThemeColor("#f8f8f8", "#313131"),
                                boxShadow: getWindowChromeThemeColor("0 2px 8px rgba(0, 0, 0, 0.04)", "0 4px 16px rgba(0, 0, 0, 0.2)"),
                            }}
                        >
                            <MuiMaterials.Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 18 }, color: text }}>Next best action</MuiMaterials.Typography>
                            <MuiMaterials.Typography sx={{ mt: 0.35, fontSize: { xs: 12, sm: 13 }, color: muted }}>{scores?.nextAction}</MuiMaterials.Typography>
                        </MuiMaterials.Box>
                        </Tip>

                        <MuiMaterials.Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {pmOpen && record.openSession && (
                            <Section id="ccr-pm" title="PM in progress" help="The visit that is open now. Check each step you did, set what you found, then complete it or discard it." paper={paper} border={border} defaultOpen>
                                {checklist.map((group) => (
                                    <MuiMaterials.Box key={group.cadence} sx={{ mb: 1.5 }}>
                                        <MuiMaterials.Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.5 }}>{CADENCE_LABEL[group.cadence] || group.cadence}</MuiMaterials.Typography>
                                        {group.items.map((item) => (
                                            <MuiMaterials.Box key={item.id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 160px 1fr" }, gap: 1, alignItems: "center", mb: 1 }}>
                                                <Tip title="Check this when the step was done on this visit." fill>
                                                <MuiMaterials.FormControlLabel
                                                    control={(
                                                        <MuiMaterials.Checkbox
                                                            checked={!!pmItems[item.id]?.checked}
                                                            onChange={(e) => setPmItems((prev) => ({
                                                                ...prev,
                                                                [item.id]: {
                                                                    checked: false,
                                                                    condition: "NotInspected",
                                                                    finding: "",
                                                                    ...prev[item.id],
                                                                    checked: e.target.checked,
                                                                },
                                                            }))}
                                                        />
                                                    )}
                                                    label={item.prompt}
                                                />
                                                </Tip>
                                                <Tip title="What you found on this step: OK, attention, or action required." fill>
                                                <MuiMaterials.TextField
                                                    select
                                                    size="small"
                                                    label="Condition"
                                                    value={pmItems[item.id]?.condition || "NotInspected"}
                                                    onChange={(e) => setPmItems((prev) => ({
                                                        ...prev,
                                                        [item.id]: {
                                                            checked: false,
                                                            condition: "NotInspected",
                                                            finding: "",
                                                            ...prev[item.id],
                                                            condition: e.target.value,
                                                        },
                                                    }))}
                                                >
                                                    {CONDITION_OPTIONS.map(([value, label]) => (
                                                        <MuiMaterials.MenuItem key={value} value={value}>{label}</MuiMaterials.MenuItem>
                                                    ))}
                                                </MuiMaterials.TextField>
                                                </Tip>
                                                <Tip title="A short note about what you saw on this step." fill>
                                                <MuiMaterials.TextField
                                                    size="small"
                                                    label="Finding"
                                                    value={pmItems[item.id]?.finding || ""}
                                                    onChange={(e) => setPmItems((prev) => ({
                                                        ...prev,
                                                        [item.id]: {
                                                            checked: false,
                                                            condition: "NotInspected",
                                                            finding: "",
                                                            ...prev[item.id],
                                                            finding: e.target.value,
                                                        },
                                                    }))}
                                                />
                                                </Tip>
                                            </MuiMaterials.Box>
                                        ))}
                                    </MuiMaterials.Box>
                                ))}
                                <Tip title="The physical points on this instrument. Set each one for this visit." fill>
                                <MuiMaterials.Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.5 }}>Field condition</MuiMaterials.Typography>
                                </Tip>
                                {(record.conditions || []).map((row) => (
                                    <MuiMaterials.Box key={row.key} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px 180px 1fr" }, gap: 1, mb: 1 }}>
                                        <Tip title={`${row.label}. This is one point checked in the field.`} fill>
                                        <MuiMaterials.Typography sx={{ alignSelf: "center" }}>{row.label}</MuiMaterials.Typography>
                                        </Tip>
                                        <Tip title="Latest status for this point: not inspected, OK, attention, or action required." fill>
                                        <MuiMaterials.TextField
                                            select
                                            size="small"
                                            value={pmConditions[row.key]?.status || row.status}
                                            onChange={(e) => setPmConditions((prev) => ({
                                                ...prev,
                                                [row.key]: { status: row.status, finding: "", ...prev[row.key], status: e.target.value },
                                            }))}
                                        >
                                            {CONDITION_OPTIONS.map(([value, label]) => (
                                                <MuiMaterials.MenuItem key={value} value={value}>{label}</MuiMaterials.MenuItem>
                                            ))}
                                        </MuiMaterials.TextField>
                                        </Tip>
                                        <Tip title="What you saw at this point." fill>
                                        <MuiMaterials.TextField
                                            size="small"
                                            label="Finding"
                                            value={pmConditions[row.key]?.finding || ""}
                                            onChange={(e) => setPmConditions((prev) => ({
                                                ...prev,
                                                [row.key]: { status: row.status, finding: "", ...prev[row.key], finding: e.target.value },
                                            }))}
                                        />
                                        </Tip>
                                    </MuiMaterials.Box>
                                ))}
                                <Tip title="Notes for the whole visit. They are stored with the completed PM." fill>
                                <MuiMaterials.TextField
                                    fullWidth
                                    size="small"
                                    label="PM notes"
                                    value={pmNotes}
                                    onChange={(e) => setPmNotes(e.target.value)}
                                    sx={{ mt: 1 }}
                                />
                                </Tip>
                                <MuiMaterials.Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                                    <Tip title="Save this visit: the checked steps, the field condition, and the notes.">
                                    <MuiMaterials.Button variant="contained" size="small" disabled={busy} onClick={completePm} sx={ccrButtonSx()}>
                                        Complete PM
                                    </MuiMaterials.Button>
                                    </Tip>
                                    <Tip title="Throw away this open visit. Nothing from it is saved.">
                                    <MuiMaterials.Button
                                        variant="contained"
                                        size="small"
                                        sx={ccrButtonSx()}
                                        disabled={busy}
                                        onClick={async () => {
                                            const next = await save({ section: "discardPm", sessionId: record.openSession.id });
                                            if (next && !next.error) setPmOpen(false);
                                        }}
                                    >
                                        Discard
                                    </MuiMaterials.Button>
                                    </Tip>
                                </MuiMaterials.Box>
                            </Section>
                        )}

                        <Section id="ccr-certainty" title="Certainty — do we know what we have?" help="Identity of this instrument. Empty fields stay To Verify and lower the Certainty score." paper={paper} border={border}>
                            <MuiMaterials.Typography sx={{ color: muted, mb: 1 }}>
                                Record completeness {scores?.certainty?.percent ?? 0}% ({scores?.certainty?.known ?? 0} of {scores?.certainty?.total ?? 0} fields).
                            </MuiMaterials.Typography>
                            {identity && (
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                                    <Tip title={FIELD_HELP.areaName} fill><MuiMaterials.TextField size="small" label="Area" value={identity.areaName} onChange={(e) => setIdentity({ ...identity, areaName: e.target.value })} /></Tip>
                                    <Tip title={FIELD_HELP.assetType} fill><MuiMaterials.TextField size="small" label="Asset Type" value={identity.assetType} onChange={(e) => setIdentity({ ...identity, assetType: e.target.value })} /></Tip>
                                    <Tip title={FIELD_HELP.status} fill>
                                    <MuiMaterials.TextField select size="small" label="Status" value={identity.status} onChange={(e) => setIdentity({ ...identity, status: e.target.value })}>
                                        {STATUS_OPTIONS.map(([value, label]) => (
                                            <MuiMaterials.MenuItem key={value} value={value}>{label}</MuiMaterials.MenuItem>
                                        ))}
                                    </MuiMaterials.TextField>
                                    </Tip>
                                    {IDENTITY_FIELDS.map(([key, label]) => (
                                        <Tip key={key} title={FIELD_HELP[key]} fill>
                                        <MuiMaterials.TextField
                                            size="small"
                                            label={label}
                                            placeholder="To Verify"
                                            value={identity[key]}
                                            onChange={(e) => setIdentity({ ...identity, [key]: e.target.value })}
                                        />
                                        </Tip>
                                    ))}
                                </MuiMaterials.Box>
                            )}
                            <Tip title="Write these identity fields onto this instrument.">
                            <MuiMaterials.Button variant="contained" size="small" sx={ccrButtonSx({ mt: 1.5 })} disabled={busy || !identity} onClick={() => save({ section: "asset", ...identity })}>
                                Save asset information
                            </MuiMaterials.Button>
                            </Tip>
                        </Section>

                        <Section id="ccr-procedure" title="Control — preventive maintenance" help="The checklist for this instrument only. Change the steps here. Start PM when you are ready to perform them." paper={paper} border={border}>
                            {!pmOpen && (
                                <MuiMaterials.Tooltip title="Start a preventive maintenance session for this instrument. Completing it saves the checklist and the field condition." arrow>
                                    <span>
                                        <MuiMaterials.Button variant="contained" size="small" sx={ccrButtonSx({ mb: 1.5 })} disabled={busy} onClick={beginPm}>
                                            Start PM
                                        </MuiMaterials.Button>
                                    </span>
                                </MuiMaterials.Tooltip>
                            )}
                            <Tip title="Where this procedure came from." fill>
                            <MuiMaterials.Typography sx={{ color: muted, mb: 1 }}>{record.pmSource}</MuiMaterials.Typography>
                            </Tip>
                            <MuiMaterials.Typography sx={{ color: muted, mb: 1.5, fontSize: 13 }}>
                                This checklist belongs to this instrument. Add a step, change its wording, or remove it. Other instruments keep their own.
                            </MuiMaterials.Typography>
                            {checklist.map((group) => (
                                <MuiMaterials.Box key={group.cadence} sx={{ mb: 1.5 }}>
                                    <MuiMaterials.Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.75 }}>{CADENCE_LABEL[group.cadence] || group.cadence}</MuiMaterials.Typography>
                                    {group.items.map((item) => (
                                        <PmStepEditor
                                            key={item.id}
                                            item={item}
                                            conditions={record.conditions || []}
                                            busy={busy}
                                            onSave={(step) => save({ section: "pmItem", pmItemId: item.id, ...step })}
                                            onRemove={() => save({ section: "removePmItem", pmItemId: item.id })}
                                        />
                                    ))}
                                </MuiMaterials.Box>
                            ))}
                            <MuiMaterials.Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.75 }}>
                                {checklist.length === 0 ? "Add the first step" : "Add a step"}
                            </MuiMaterials.Typography>
                            <PmStepEditor
                                conditions={record.conditions || []}
                                busy={busy}
                                onSave={(step) => save({ section: "pmItem", ...step })}
                            />
                            <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.5, mt: 1 }}>
                                <Line label="Last Inspection" value={record.pmStatus?.lastInspection} help="Date of the last completed step marked as an inspection." />
                                <Line label="Last Cleaning" value={record.pmStatus?.lastCleaning} help="Date of the last completed step marked as a cleaning." />
                                <Line label="Last Adjustment" value={record.pmStatus?.lastAdjustment} help="Date of the last completed step marked as an adjustment." />
                                <Line label="Last Calibration/Verification" value={record.pmStatus?.lastCalibration} help="Date of the last calibration condition or calibration event." />
                                <Line label="Next Required Action" value={record.pmStatus?.nextAction} help="Whether a baseline inspection is still needed." />
                            </MuiMaterials.Box>
                        </Section>

                        <Section id="ccr-condition" title="Condition — latest field inspection" help="The newest result for each inspection point. Completing a PM writes these rows." paper={paper} border={border}>
                            {(record.conditions || []).map((row) => (
                                <Tip key={row.key} title={`${row.label}: the newest status and finding. Change it while a PM is in progress.`} fill>
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.4fr", gap: 1, py: 0.4, borderBottom: `1px solid ${border}` }}>
                                    <span>{row.label}</span>
                                    <span>{toneOf(row.status === "Ok" ? { tone: "operational" } : row.status === "Attention" ? { tone: "attention" } : row.status === "Action" ? { tone: "action" } : { tone: "unknown" }).mark} {row.display}</span>
                                    <span>{row.finding || "—"}</span>
                                </MuiMaterials.Box>
                                </Tip>
                            ))}
                            <MuiMaterials.Typography sx={{ mt: 1, fontWeight: 700 }}>
                                Overall: {toneOf(scores?.condition).mark} {scores?.condition?.label}
                            </MuiMaterials.Typography>
                        </Section>

                        <Section id="ccr-relief" title="Relief — if this instrument fails, are we prepared?" help="How operations continue if this instrument fails: spare, substitute, lead time, and who can replace it." paper={paper} border={border}>
                            {relief && (
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                                    {RELIEF_FIELDS.map(([key, label]) => (
                                        <Tip key={key} title={RELIEF_HELP[key]} fill>
                                        <MuiMaterials.TextField
                                            size="small"
                                            label={label}
                                            placeholder="Unknown"
                                            value={relief[key]}
                                            onChange={(e) => setRelief({ ...relief, [key]: e.target.value })}
                                        />
                                        </Tip>
                                    ))}
                                </MuiMaterials.Box>
                            )}
                            <Tip title="Save how operations continue if this instrument fails.">
                            <MuiMaterials.Button variant="contained" size="small" sx={ccrButtonSx({ mt: 1.5 })} disabled={busy || !relief} onClick={() => save({ section: "relief", ...relief })}>
                                Save recovery answers
                            </MuiMaterials.Button>
                            </Tip>
                        </Section>

                        <Section id="ccr-signal" title="Signal path" help="The path from this instrument to the person who uses the reading. Click a tile to write what that step is." paper={paper} border={border}>
                            <MuiMaterials.Box
                                className="hide-scrollbar"
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    overflowX: "auto",
                                    pb: 1,
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                    "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 },
                                }}
                            >
                                {(record.signalPath || []).map((node, index) => (
                                    <MuiMaterials.Box key={node.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Tip title={`${node.label}. Click to write what is known about this step.`}>
                                        <MuiMaterials.Button
                                            variant="outlined"
                                            onClick={() => {
                                                setSignalId(node.id);
                                                setSignalDetail(node.detail || "");
                                            }}
                                            sx={{
                                                ...outlinedActionButtonSx(),
                                                minWidth: 140,
                                                minHeight: 72,
                                                textTransform: "none",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <span style={{ fontWeight: 800 }}>{node.label}</span>
                                            <span style={{ color: muted }}>{node.unknown ? "?" : node.detail}</span>
                                        </MuiMaterials.Button>
                                        </Tip>
                                        {index < record.signalPath.length - 1 && <span>→</span>}
                                    </MuiMaterials.Box>
                                ))}
                            </MuiMaterials.Box>
                            {signalId != null && (
                                <MuiMaterials.Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                                    <Tip title="What is known about the signal-path step you clicked." fill>
                                    <MuiMaterials.TextField size="small" fullWidth label="What we know about this step" value={signalDetail} onChange={(e) => setSignalDetail(e.target.value)} />
                                    </Tip>
                                    <Tip title="Save the note for the step you clicked.">
                                    <MuiMaterials.Button variant="contained" size="small" sx={ccrButtonSx()} disabled={busy} onClick={() => save({ section: "signal", nodeId: signalId, detail: signalDetail })}>
                                        Save step
                                    </MuiMaterials.Button>
                                    </Tip>
                                </MuiMaterials.Box>
                            )}
                        </Section>

                        <Section id="ccr-documents" title="Document center" help="Each area holds one file or one web address for this asset. Open sends it to the device viewer." paper={paper} border={border}>
                            <MuiMaterials.Typography sx={{ color: muted, mb: 1.5, fontSize: 13 }}>
                                One file or web address per area. Take a photo, attach a file, or paste a link.
                            </MuiMaterials.Typography>
                            <MuiMaterials.Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {(record.documents || []).map((doc) => (
                                    <MuiMaterials.Box
                                        key={doc.id}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Tip title={`${doc.category} for this asset. Attach a file, paste a link, or open it.`}>
                                            <span>
                                                <Line label={doc.category} value={doc.display} />
                                            </span>
                                        </Tip>
                                        <MuiMaterials.Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
                                            <UrlButton
                                                title={`Paste a web address for ${doc.category}.`}
                                                value={doc.url || ""}
                                                busy={busy}
                                                onSave={async (url) => {
                                                    const next = await save({ section: "documentUrl", documentId: doc.id, url });
                                                    return next && !next.error;
                                                }}
                                            />
                                            <Tip title={doc.attached ? `Replace the file in ${doc.category}.` : `Choose a file already on this phone for ${doc.category}.`}>
                                                <MuiMaterials.Button
                                                    variant="contained"
                                                    size="small"
                                                    sx={ccrButtonSx()}
                                                    disabled={busy}
                                                    onClick={() => {
                                                        docTargetRef.current = doc.id;
                                                        docFileRef.current?.click();
                                                    }}
                                                >
                                                    Attach
                                                </MuiMaterials.Button>
                                            </Tip>
                                            <Tip title={`Open the phone camera and save the picture in ${doc.category}.`}>
                                                <MuiMaterials.Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={outlinedActionButtonSx()}
                                                    disabled={busy}
                                                    onClick={() => {
                                                        cameraTargetRef.current = doc.id;
                                                        cameraRef.current?.click();
                                                    }}
                                                >
                                                    Take photo
                                                </MuiMaterials.Button>
                                            </Tip>
                                            {doc.attached ? (
                                                <Tip title={`Open ${doc.fileName || doc.category}.`}>
                                                    <MuiMaterials.Button
                                                        variant="outlined"
                                                        size="small"
                                                        sx={outlinedActionButtonSx()}
                                                        disabled={busy}
                                                        onClick={() => openDocument(doc)}
                                                    >
                                                        Open
                                                    </MuiMaterials.Button>
                                                </Tip>
                                            ) : null}
                                            {doc.attached ? (
                                                <Tip title={`Remove the file from ${doc.category}.`}>
                                                    <MuiMaterials.Button
                                                        variant="outlined"
                                                        size="small"
                                                        sx={outlinedActionButtonSx()}
                                                        disabled={busy}
                                                        onClick={() => save({ section: "removeDocument", documentId: doc.id })}
                                                    >
                                                        Remove
                                                    </MuiMaterials.Button>
                                                </Tip>
                                            ) : null}
                                        </MuiMaterials.Box>
                                    </MuiMaterials.Box>
                                ))}
                            </MuiMaterials.Box>
                        </Section>

                        <Section id="ccr-spare" title="Supported spare" help="The spare kept for this instrument. These lines are the spare itself, separate from the recovery answers in Relief." paper={paper} border={border}>
                            <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "8px 16px" }}>
                                <Line label="Part" value={record.spare?.part} help="Part number of the spare kept for this instrument." />
                                <Line label="Quantity" value={record.spare?.quantity} help="How many of that spare are on hand." />
                                <Line label="Location" value={record.spare?.location} help="Where the spare is stored." />
                                <Line label="Condition" value={record.spare?.condition} help="Whether the spare is known to be good." />
                                <Line label="Last Verified" value={record.spare?.lastVerified} help="When someone last checked the spare." />
                                <Line label="Lead Time" value={record.spare?.leadTime} help="How long it takes to get another one." />
                            </MuiMaterials.Box>
                        </Section>

                        <Section id="ccr-media" title="Photos and videos" help="Photos and videos of this physical unit. Take one, add a file, or paste a web address. Click a picture or video to open it in the device viewer." paper={paper} border={border}>
                            <MuiMaterials.Typography sx={{ color: muted, mb: 1 }}>
                                Take a picture or video, add a file, or paste a link. Click one to open it in the device viewer.
                            </MuiMaterials.Typography>
                            <MuiMaterials.Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Tip title="Open the phone camera and save the picture on this instrument.">
                            <MuiMaterials.Button
                                variant="contained"
                                size="small"
                                sx={containedActionButtonSx()}
                                disabled={busy}
                                onClick={() => {
                                    cameraTargetRef.current = "media";
                                    cameraRef.current?.click();
                                }}
                            >
                                Take photo
                            </MuiMaterials.Button>
                            </Tip>
                            <Tip title="Open the phone camera, record a video, and save it on this instrument.">
                            <MuiMaterials.Button
                                variant="contained"
                                size="small"
                                sx={containedActionButtonSx()}
                                disabled={busy}
                                onClick={() => videoRef.current?.click()}
                            >
                                Take video
                            </MuiMaterials.Button>
                            </Tip>
                            <Tip title="Choose a photo or video already on this phone. It stays on this record.">
                            <MuiMaterials.Button
                                variant="outlined"
                                size="small"
                                sx={outlinedActionButtonSx()}
                                disabled={busy}
                                onClick={() => fileRef.current?.click()}
                            >
                                Add photo or video
                            </MuiMaterials.Button>
                            </Tip>
                                <UrlButton
                                    title="Paste a web address for a photo or video on this instrument."
                                    busy={busy}
                                    showKind
                                    onSave={async (url, kind) => {
                                        const next = await save({ section: "mediaUrl", url, kind });
                                        return next && !next.error;
                                    }}
                                />
                            </MuiMaterials.Box>
                            {uploadNote ? (
                                <MuiMaterials.Typography sx={{ mt: 1, fontSize: 13 }}>{uploadNote}</MuiMaterials.Typography>
                            ) : null}
                            <MediaGallery
                                siteGuid={siteGuid}
                                items={record.media || []}
                                busy={busy}
                                onRemove={(mediaId) => save({ section: "removeMedia", mediaId })}
                            />
                        </Section>

                        <Section id="ccr-event" title="History / CCR log" help="A log of what happened. Adding an event records a note. It does not change the checklist, the inspection, or the spare." paper={paper} border={border}>
                            <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "90px 110px 1fr 1fr 120px", gap: 1, fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                                <span>Date</span><span>Event</span><span>Finding</span><span>Action</span><span>Person</span>
                            </MuiMaterials.Box>
                            {(record.events || []).map((row) => (
                                <Tip key={row.id} title={`${row.date} ${row.eventType}. ${row.finding || "No finding"}. ${row.action || "No action"}.`} fill>
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "90px 110px 1fr 1fr 120px", gap: 1, fontSize: 13, py: 0.4, borderTop: `1px solid ${border}` }}>
                                    <span>{row.date}</span>
                                    <span>{row.eventType}</span>
                                    <span>{row.finding || "—"}</span>
                                    <span>{row.action || "—"}</span>
                                    <span>{row.person || "—"}</span>
                                </MuiMaterials.Box>
                                </Tip>
                            ))}
                            <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "160px 1fr 1fr" }, gap: 1, mt: 1.5 }}>
                                <Tip title="Kind of note: inspection, PM, calibration, repair, photo, or a general note." fill>
                                <MuiMaterials.TextField select size="small" label="Event" value={eventType} onChange={(e) => setEventType(e.target.value)}>
                                    {EVENT_TYPES.map((value) => (
                                        <MuiMaterials.MenuItem key={value} value={value}>{value}</MuiMaterials.MenuItem>
                                    ))}
                                </MuiMaterials.TextField>
                                </Tip>
                                <Tip title="What was found. This is a log line, not a checklist step." fill>
                                <MuiMaterials.TextField size="small" label="Finding" value={eventFinding} onChange={(e) => setEventFinding(e.target.value)} />
                                </Tip>
                                <Tip title="What was done about it." fill>
                                <MuiMaterials.TextField size="small" label="Action" value={eventAction} onChange={(e) => setEventAction(e.target.value)} />
                                </Tip>
                            </MuiMaterials.Box>
                            <Tip title="Add this note to the log for this instrument.">
                            <MuiMaterials.Button
                                variant="contained"
                                size="small"
                                sx={ccrButtonSx({ mt: 1 })}
                                disabled={busy}
                                onClick={async () => {
                                    const next = await save({ section: "event", eventType, finding: eventFinding, action: eventAction });
                                    if (next && !next.error) {
                                        setEventFinding("");
                                        setEventAction("");
                                    }
                                }}
                            >
                                + Add event
                            </MuiMaterials.Button>
                            </Tip>
                        </Section>
                        </MuiMaterials.Box>
                    </>
                )}
            </MuiMaterials.Box>
            </MuiMaterials.Box>
            </CcrSectionContext.Provider>
        </WindowChrome>
        {labelOpen && asset?.assetTag ? (
            <MuiMaterials.Modal className="ccr-screen-only" open onClose={() => setLabelOpen(false)}>
                <MuiMaterials.Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "#fff",
                        color: "#111",
                        WebkitTextFillColor: "#111",
                        p: 3,
                        borderRadius: 1,
                        maxWidth: 420,
                    }}
                >
                    <CcrLabelContent tag={asset.assetTag} url={labelUrl} lines={labelLines} />
                    <MuiMaterials.Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Tip title="Send the dashed sticker and the reference lines to the printer.">
                            <MuiMaterials.Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={printLabel}>
                                Print
                            </MuiMaterials.Button>
                        </Tip>
                        <Tip title="Close this preview without printing.">
                            <MuiMaterials.Button size="small" variant="outlined" onClick={() => setLabelOpen(false)}>
                                Close
                            </MuiMaterials.Button>
                        </Tip>
                    </MuiMaterials.Stack>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        ) : null}
        </>
    );
}

const SCORE_HELP = {
    Certainty: "How completely this instrument is identified. The percent is filled identity fields out of the fields this record asks for.",
    Control: "Whether preventive maintenance is known and in use. Red means no procedure is identified, or a PM has not been completed.",
    Relief: "Whether operations can continue if this instrument fails: a spare, a bypass, a manual method, and who responds.",
    Condition: "What the latest field inspection found. Unknown means this instrument has not been inspected yet.",
};

const SCORE_ICONS = {
    Certainty: FactCheckOutlinedIcon,
    Control: BuildOutlinedIcon,
    Relief: ShieldOutlinedIcon,
    Condition: TroubleshootOutlinedIcon,
};

function ScoreCard({ name, score, detail }) {
    const tone = toneOf(score);
    const text = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const subtle = getWindowChromeThemeColor("#666666", "#a8abb1");
    const cardBg = getWindowChromeThemeColor("#ffffff", "#383838");
    const border = getWindowChromeThemeColor("rgba(0, 0, 0, 0.1)", "rgba(244, 204, 142, 0.22)");
    const Icon = SCORE_ICONS[name];
    return (
        <MuiMaterials.Tooltip title={SCORE_HELP[name] || name} arrow placement="top">
            <MuiMaterials.Box
                sx={{
                    width: "100%",
                    height: "100%",
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: "10px",
                    border: `1px solid ${border}`,
                    background: cardBg,
                    boxShadow: getWindowChromeThemeColor("0 1px 3px rgba(0, 0, 0, 0.06)", "0 2px 8px rgba(0, 0, 0, 0.25)"),
                    boxSizing: "border-box",
                    cursor: "help",
                }}
            >
                <MuiMaterials.Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                    <MuiMaterials.Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            flexShrink: 0,
                            bgcolor: getWindowChromeThemeColor("rgba(0, 0, 0, 0.05)", "rgba(244, 204, 142, 0.12)"),
                            color: tone.color,
                        }}
                    >
                        <Icon sx={{ fontSize: 20, color: tone.color }} />
                    </MuiMaterials.Box>
                    <MuiMaterials.Box sx={{ minWidth: 0, flex: 1 }}>
                        <MuiMaterials.Typography
                            sx={{
                                fontSize: { xs: 10, sm: 11 },
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.55px",
                                color: subtle,
                                mb: 0.35,
                            }}
                        >
                            {name}
                        </MuiMaterials.Typography>
                        <MuiMaterials.Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: 15, sm: 18 },
                                color: text,
                                WebkitTextFillColor: text,
                                lineHeight: 1.2,
                            }}
                        >
                            {detail || "—"}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        </MuiMaterials.Tooltip>
    );
}

function PmStepEditor({ item, conditions, busy, onSave, onRemove }) {
    const [cadence, setCadence] = useState(item?.cadence || "Daily");
    const [prompt, setPrompt] = useState(item?.prompt || "");
    const [resultKind, setResultKind] = useState(item?.resultKind || "");
    const [conditionKey, setConditionKey] = useState(item?.conditionKey || "");

    useEffect(() => {
        setCadence(item?.cadence || "Daily");
        setPrompt(item?.prompt || "");
        setResultKind(item?.resultKind || "");
        setConditionKey(item?.conditionKey || "");
    }, [item?.id, item?.cadence, item?.prompt, item?.resultKind, item?.conditionKey]);

    const saveStep = async () => {
        const next = await onSave({ cadence, prompt, resultKind, conditionKey });
        if (!item && next && !next.error) {
            setPrompt("");
            setResultKind("");
            setConditionKey("");
        }
    };

    return (
        <MuiMaterials.Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "150px minmax(0, 1.6fr) 180px 160px auto" },
                gap: 1,
                alignItems: "center",
                mb: 1,
            }}
        >
            <Tip title="How often this step is done: daily, weekly, or monthly." fill>
            <MuiMaterials.TextField select size="small" label="Cadence" value={cadence} onChange={(e) => setCadence(e.target.value)}>
                {CADENCE_OPTIONS.map(([value, label]) => (
                    <MuiMaterials.MenuItem key={value} value={value}>{label}</MuiMaterials.MenuItem>
                ))}
            </MuiMaterials.TextField>
            </Tip>
            <Tip title="The instruction for this step, on this instrument only." fill>
            <MuiMaterials.TextField
                size="small"
                label="Step"
                placeholder="What to check, clean, or adjust"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
            />
            </Tip>
            <Tip title="Which field-condition row this step updates when the PM is completed." fill>
            <MuiMaterials.TextField select size="small" label="Updates condition" value={conditionKey} onChange={(e) => setConditionKey(e.target.value)}>
                <MuiMaterials.MenuItem value="">None</MuiMaterials.MenuItem>
                {conditions.map((row) => (
                    <MuiMaterials.MenuItem key={row.key} value={row.key}>{row.label}</MuiMaterials.MenuItem>
                ))}
            </MuiMaterials.TextField>
            </Tip>
            <Tip title="Inspection, cleaning, or adjustment. That is what sets the last-done date." fill>
            <MuiMaterials.TextField select size="small" label="Counts as" value={resultKind} onChange={(e) => setResultKind(e.target.value)}>
                {RESULT_KIND_OPTIONS.map(([value, label]) => (
                    <MuiMaterials.MenuItem key={value || "none"} value={value}>{label}</MuiMaterials.MenuItem>
                ))}
            </MuiMaterials.TextField>
            </Tip>
            <MuiMaterials.Box sx={{ display: "flex", gap: 1 }}>
                <Tip title={item ? "Save changes to this step." : "Add this step to this instrument's checklist."}>
                <MuiMaterials.Button variant="contained" size="small" sx={ccrButtonSx()} disabled={busy || !prompt.trim()} onClick={saveStep}>
                    {item ? "Save" : "Add step"}
                </MuiMaterials.Button>
                </Tip>
                {item && (
                    <Tip title="Remove this step from this instrument. Other instruments are unchanged.">
                    <MuiMaterials.Button variant="outlined" size="small" sx={outlinedActionButtonSx()} disabled={busy} onClick={onRemove}>
                        Remove
                    </MuiMaterials.Button>
                    </Tip>
                )}
            </MuiMaterials.Box>
        </MuiMaterials.Box>
    );
}

function Section({ id, title, help, children, defaultOpen = false }) {
    const ctx = useContext(CcrSectionContext);
    const open = ctx.isOpen(id, defaultOpen);
    const text = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const border = getWindowChromeThemeColor("#e0e0e0", "#3d3d3d");
    const fieldBg = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const contentBg = getWindowChromeThemeColor("#e8e8e8", "#1e1e1e");
    const fields = comparisonFieldSx();
    return (
        <MuiMaterials.Box
            id={id}
            sx={{
                flexShrink: 0,
                alignSelf: "stretch",
                background: fieldBg,
                border: `1px solid ${border}`,
                borderRadius: "8px",
                overflow: "hidden",
            }}
        >
            <Tip title={help} fill>
            <MuiMaterials.Box
                component="button"
                type="button"
                aria-expanded={open}
                onClick={() => ctx.toggle(id, defaultOpen)}
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    m: 0,
                    border: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    background: "transparent",
                    color: text,
                    WebkitTextFillColor: text,
                    fontFamily: "inherit",
                    "&:hover": {
                        background: getWindowChromeThemeColor("rgba(0,0,0,0.03)", "rgba(255,255,255,0.03)"),
                    },
                }}
            >
                {open
                    ? <MuiMaterials.ExpandMoreIcon sx={{ color: text, fontSize: 22 }} />
                    : <MuiMaterials.ChevronRightIcon sx={{ color: text, fontSize: 22 }} />}
                <span style={{ color: text, WebkitTextFillColor: text, fontSize: 14, fontWeight: 600 }}>{title}</span>
            </MuiMaterials.Box>
            </Tip>
            <MuiMaterials.Collapse in={open}>
                <MuiMaterials.Box
                    sx={{
                        padding: "12px 16px 16px",
                        borderTop: `1px solid ${border}`,
                        backgroundColor: contentBg,
                        color: text,
                        "& .MuiTypography-root": {
                            color: text,
                            WebkitTextFillColor: text,
                        },
                        "& .MuiFormControlLabel-label": {
                            color: text,
                            WebkitTextFillColor: text,
                        },
                        "& .MuiOutlinedInput-root": {
                            ...fields["& .MuiOutlinedInput-root"],
                            minHeight: 40,
                        },
                        "& .MuiInputLabel-root": fields["& .MuiInputLabel-root"],
                        "& .MuiInputLabel-root.Mui-focused": fields["& .MuiInputLabel-root.Mui-focused"],
                        "& .MuiInputBase-input": {
                            ...fields["& .MuiInputBase-input"],
                            height: "auto",
                            minHeight: 22,
                            WebkitTextFillColor: text,
                        },
                        "& .MuiSelect-select": {
                            ...fields["& .MuiSelect-select"],
                            minHeight: 22,
                            display: "flex",
                            alignItems: "center",
                            WebkitTextFillColor: text,
                        },
                        "& .MuiSvgIcon-root": fields["& .MuiSvgIcon-root"],
                    }}
                >
                    {children}
                </MuiMaterials.Box>
            </MuiMaterials.Collapse>
        </MuiMaterials.Box>
    );
}

function UrlButton({ title, value = "", busy, showKind = false, onSave }) {
    const [anchor, setAnchor] = useState(null);
    const [url, setUrl] = useState(value || "");
    const [kind, setKind] = useState("Photo");
    const paper = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const text = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const border = getWindowChromeThemeColor("#d0d0d0", "#f4cc8e55");
    const open = (event) => {
        setUrl(value || "");
        setKind("Photo");
        setAnchor(event.currentTarget);
    };
    return (
        <>
            <Tip title={title}>
                <MuiMaterials.Button
                    variant="outlined"
                    size="small"
                    disabled={busy}
                    sx={outlinedActionButtonSx()}
                    onClick={open}
                >
                    Url
                </MuiMaterials.Button>
            </Tip>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        p: 1.5,
                        mt: 0.5,
                        background: paper,
                        color: text,
                        border: `1px solid ${border}`,
                        maxWidth: "min(96vw, 520px)",
                    },
                }}
            >
                <MuiMaterials.Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    <MuiMaterials.TextField
                        size="small"
                        label="Web address"
                        placeholder="https://"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        sx={{ ...addFieldSx(), width: { xs: "100%", sm: 320 }, flex: "1 1 240px" }}
                    />
                    {showKind ? (
                        <MuiMaterials.TextField select size="small" label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} sx={addFieldSx()}>
                            <MuiMaterials.MenuItem value="Photo">Photo</MuiMaterials.MenuItem>
                            <MuiMaterials.MenuItem value="Video">Video</MuiMaterials.MenuItem>
                        </MuiMaterials.TextField>
                    ) : null}
                    <MuiMaterials.Button
                        variant="contained"
                        size="small"
                        disabled={busy}
                        sx={ccrButtonSx({ flexShrink: 0 })}
                        onClick={async () => {
                            const saved = await onSave(url.trim(), kind);
                            if (saved) setAnchor(null);
                        }}
                    >
                        Save
                    </MuiMaterials.Button>
                </MuiMaterials.Box>
            </Popover>
        </>
    );
}

function MediaGallery({ siteGuid, items, busy, onRemove }) {
    const [urls, setUrls] = useState({});
    const key = (items || []).map((item) => `${item.id}:${item.url || ""}`).join(",");
    useEffect(() => {
        let cancelled = false;
        const created = [];
        (async () => {
            const next = {};
            for (const item of items || []) {
                if (item.url) continue;
                try {
                    const response = await fetch(`${global.ApiUrl}/api/ccr-media/${siteGuid}/${item.id}`, {
                        headers: { Authorization: `Bearer ${global.gw.token}` },
                    });
                    if (!response.ok) continue;
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    created.push(url);
                    next[item.id] = url;
                } catch {
                    // Leave a missing file as a named row without a preview.
                }
            }
            if (!cancelled) setUrls(next);
        })();
        return () => {
            cancelled = true;
            created.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [siteGuid, key]);
    if (!items?.length) {
        return (
            <MuiMaterials.Typography sx={{ mt: 1, fontSize: 13, opacity: 0.8 }}>
                No photos or videos yet.
            </MuiMaterials.Typography>
        );
    }
    return (
        <MuiMaterials.Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
            {items.map((item) => (
                <MuiMaterials.Box key={item.id} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <MuiMaterials.Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {(item.url ? item.kind : item.fileName) || item.kind} · {item.createdAt}
                        </MuiMaterials.Typography>
                        <Tip title="Remove this photo or video from this instrument.">
                        <MuiMaterials.Button
                            variant="outlined"
                            size="small"
                            disabled={busy}
                            sx={outlinedActionButtonSx()}
                            onClick={() => onRemove(item.id)}
                        >
                            Remove
                        </MuiMaterials.Button>
                        </Tip>
                    </MuiMaterials.Box>
                    <MediaPreview item={item} href={item.url || urls[item.id] || ""} />
                </MuiMaterials.Box>
            ))}
        </MuiMaterials.Box>
    );
}

function MediaPreview({ item, href }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [href]);
    if (!href) return null;
    const video = item.kind === "Video";
    const linked = Boolean(item.url) && href === item.url;
    const showVideo = video && !failed && (linked || href.startsWith("blob:") || /\.(mp4|mov|m4v|webm|qt)(\?|$)/i.test(href));
    const showImage = !video && !failed && (linked || href.startsWith("blob:") || /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(href));
    const tile = linked
        ? { width: "100%", maxWidth: 320, aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 8, background: "#111", display: "block" }
        : { maxWidth: "100%", maxHeight: 360, objectFit: "contain", display: "block" };
    return (
        <Tip title={video ? "Open this video in the device viewer." : "Open this photo in the device viewer."} fill>
            <MuiMaterials.Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "block", cursor: "pointer", maxWidth: linked ? 320 : "100%", color: "inherit" }}
            >
                {showVideo ? (
                    <video
                        src={href}
                        muted
                        playsInline
                        preload="metadata"
                        onError={() => setFailed(true)}
                        style={{ ...tile, pointerEvents: "none" }}
                    />
                ) : null}
                {showImage ? (
                    <img
                        src={href}
                        alt={item.kind === "Photo" ? "Instrument photo" : (item.fileName || "Instrument photo")}
                        onError={() => setFailed(true)}
                        style={{ ...tile, pointerEvents: "none" }}
                    />
                ) : null}
                {!showVideo && !showImage ? (
                    <span style={{ textDecoration: "underline" }}>{item.url || item.fileName || "Open"}</span>
                ) : null}
            </MuiMaterials.Box>
        </Tip>
    );
}

function Line({ label, value, help }) {
    const text = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const subtle = getWindowChromeThemeColor("#666666", "#a8abb1");
    const body = (
        <MuiMaterials.Box sx={{ py: 0.35 }}>
            <MuiMaterials.Typography sx={{ color: subtle, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {label}
            </MuiMaterials.Typography>
            <MuiMaterials.Typography sx={{ color: text, WebkitTextFillColor: text, fontSize: 14, fontWeight: 500 }}>
                {value || "—"}
            </MuiMaterials.Typography>
        </MuiMaterials.Box>
    );
    return help ? <Tip title={help} fill>{body}</Tip> : body;
}

function AddInstrument({ newTag, newArea, newType, newPart, newSerial, setNewTag, setNewArea, setNewType, setNewPart, setNewSerial, busy, onAdd }) {
    const fieldSx = addFieldSx();
    return (
        <MuiMaterials.Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Tip title="Unique name for this unit. It cannot match a tag already on this site."><MuiMaterials.TextField size="small" label="Asset tag" value={newTag} onChange={(e) => setNewTag(e.target.value)} sx={fieldSx} /></Tip>
            <Tip title={FIELD_HELP.areaName}><MuiMaterials.TextField size="small" label="Area" value={newArea} onChange={(e) => setNewArea(e.target.value)} sx={fieldSx} /></Tip>
            <Tip title={FIELD_HELP.assetType}><MuiMaterials.TextField size="small" label="Asset type" value={newType} onChange={(e) => setNewType(e.target.value)} sx={fieldSx} /></Tip>
            <Tip title={FIELD_HELP.partNumber}><MuiMaterials.TextField size="small" label="Part number" value={newPart} onChange={(e) => setNewPart(e.target.value)} sx={fieldSx} /></Tip>
            <Tip title={FIELD_HELP.serialNumber}><MuiMaterials.TextField size="small" label="Serial number" value={newSerial} onChange={(e) => setNewSerial(e.target.value)} sx={fieldSx} /></Tip>
            <Tip title="Create this instrument and open its CCR. The tag has to be new.">
            <MuiMaterials.Button
                variant="contained"
                size="small"
                disabled={busy || !newTag.trim()}
                onClick={onAdd}
                sx={ccrButtonSx({ flexShrink: 0 })}
            >
                Create record
            </MuiMaterials.Button>
            </Tip>
        </MuiMaterials.Box>
    );
}
